/**
 * Admin Panel
 *
 * Shows all users and their character assignments.
 * Only accessible to admin users (brottler@gmail.com).
 */

import { useState, useEffect } from 'react';
import { isAdminEmail } from '@machina/core';
import { trpc } from '../../lib/trpc';
import { useAuthStore } from '../../stores/authStore';
import { GouldCorner } from '../Ornaments';
import { Icon } from '../Icon';
import { BetaKeysPanel } from './BetaKeysPanel';
import { BugReportsPanel } from './BugReportsPanel';

type AdminTab = 'users' | 'betaKeys' | 'bugs';

// Client-facing shapes returned by trpc.admin.*. Authz is enforced server-side
// by adminProcedure; the isAdmin() gate below only decides whether to render.
interface MachinaUser {
  id: string;
  displayName: string;
  email: string | null;
  createdAt: string | null;
}

interface MachinaCharacter {
  id: string;
  userId: string;
  campaignId: string | null;
  data: {
    name: string;
    race?: string;
    playerName?: string;
    classes?: Array<{ name: string; level: number; subclass?: string }>;
  };
  createdAt: string | null;
}

interface AdminPanelProps {
  onClose: () => void;
}

// A destructive character move awaiting explicit confirmation.
type PendingCharacterAction =
  | {
      kind: 'reassign';
      characterId: string;
      characterLabel: string;
      fromUserName: string;
      toUserId: string;
      toUserName: string;
    }
  | {
      kind: 'changeCampaign';
      characterId: string;
      characterLabel: string;
      fromCampaignName: string;
      toCampaignId: string | null;
      toCampaignName: string;
    };

export function isAdmin(email: string | undefined): boolean {
  return isAdminEmail(email);
}

export function AdminPanel({ onClose }: AdminPanelProps) {
  const { user } = useAuthStore();
  const [users, setUsers] = useState<MachinaUser[]>([]);
  const [characters, setCharacters] = useState<MachinaCharacter[]>([]);
  const [campaigns, setCampaigns] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reassigning, setReassigning] = useState<string | null>(null);
  const [changingCampaign, setChangingCampaign] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<AdminTab>('users');

  // Confirmation gate for destructive character moves (reassign owner /
  // change campaign). Both fire from <select> onChange with no native undo,
  // so capture the requested change and require explicit confirmation first.
  const [pendingAction, setPendingAction] = useState<PendingCharacterAction | null>(null);

  // Verify admin access
  if (!isAdmin(user?.email)) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <h2 style={styles.title}>Access Denied</h2>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            You do not have admin privileges.
          </p>
          <button onClick={onClose} style={styles.closeButton}>Close</button>
        </div>
      </div>
    );
  }

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setIsLoading(true);
    setError(null);

    try {
      // All reads go through the service-role admin endpoint — the browser anon
      // client would be RLS-scoped to the admin's own rows.
      const [usersData, charsData, campaignsData] = await Promise.all([
        trpc.admin.listUsers.query(),
        trpc.admin.listCharacters.query(),
        trpc.admin.listCampaigns.query(),
      ]);

      setUsers(usersData);
      setCharacters(charsData);
      setCampaigns(campaignsData);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleReassignCharacter(characterId: string, newUserId: string) {
    if (!newUserId) return;

    setReassigning(characterId);
    try {
      // Server updates user_id + playerName and reconciles campaign membership.
      await trpc.admin.reassignCharacter.mutate({ characterId, userId: newUserId });

      // Refresh data
      await loadData();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setReassigning(null);
      setSelectedUserId('');
    }
  }

  async function handleChangeCampaign(characterId: string, newCampaignId: string | null) {
    setChangingCampaign(characterId);
    try {
      // Server updates both the campaign_id column and data.campaignId and
      // reconciles campaign membership.
      await trpc.admin.changeCharacterCampaign.mutate({ characterId, campaignId: newCampaignId });

      // Refresh data
      await loadData();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setChangingCampaign(null);
    }
  }

  async function ensureCampaignMember(campaignId: string, userId: string) {
    await trpc.admin.ensureCampaignMember.mutate({ campaignId, userId, role: 'player' });
  }

  // Runs the confirmed pending action via the existing (unchanged) handlers.
  async function runPendingAction() {
    if (!pendingAction) return;
    const action = pendingAction;
    setPendingAction(null);
    if (action.kind === 'reassign') {
      await handleReassignCharacter(action.characterId, action.toUserId);
    } else {
      await handleChangeCampaign(action.characterId, action.toCampaignId);
    }
  }

  function campaignNameById(campaignId: string | null): string {
    if (!campaignId) return 'No Campaign';
    return campaigns.find((c) => c.id === campaignId)?.name ?? 'Unknown Campaign';
  }

  function getCharactersForUser(userId: string) {
    return characters.filter((c) => c.userId === userId);
  }

  function formatCharacterInfo(char: MachinaCharacter) {
    const data = char.data;
    const classInfo = data.classes?.map((c) => `${c.name} ${c.level}`).join('/') || 'Unknown';
    return `${data.name} (${data.race || 'Unknown'} ${classInfo})`;
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <GouldCorner size={20} style={{ position: 'absolute', top: 4, left: 4 }} />
          <GouldCorner size={20} style={{ position: 'absolute', top: 4, right: 4, transform: 'scaleX(-1)' }} />

          <h2 style={styles.title}>
            <Icon name="crown" size={24} color="#d4af37" />
            Admin Panel
          </h2>

          <button onClick={onClose} style={styles.closeIconButton}>
            <Icon name="close" size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={styles.tabBar}>
          <button
            onClick={() => setActiveTab('users')}
            style={activeTab === 'users' ? styles.tabActive : styles.tab}
          >
            <Icon name="users" size={14} />
            Users
          </button>
          <button
            onClick={() => setActiveTab('betaKeys')}
            style={activeTab === 'betaKeys' ? styles.tabActive : styles.tab}
          >
            <Icon name="lock" size={14} />
            Beta Keys
          </button>
          <button
            onClick={() => setActiveTab('bugs')}
            style={activeTab === 'bugs' ? styles.tabActive : styles.tab}
          >
            <Icon name="bug" size={14} />
            Bug Reports
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {activeTab === 'betaKeys' && <BetaKeysPanel />}

          {activeTab === 'bugs' && <BugReportsPanel />}

          {activeTab === 'users' && (
            <>
              {isLoading && (
                <div style={styles.loading}>
                  <Icon name="loader" size={24} />
                  Loading users and characters...
                </div>
              )}

              {error && (
                <div style={styles.error}>
                  <Icon name="alert" size={16} />
                  {error}
                </div>
              )}

              {!isLoading && !error && (
                <>
                  {/* Stats summary */}
                  <div style={styles.stats}>
                    <div style={styles.stat}>
                      <span style={styles.statNumber}>{users.length}</span>
                      <span style={styles.statLabel}>Users</span>
                    </div>
                    <div style={styles.stat}>
                      <span style={styles.statNumber}>{characters.length}</span>
                      <span style={styles.statLabel}>Characters</span>
                    </div>
                    <div style={styles.stat}>
                      <span style={styles.statNumber}>{campaigns.length}</span>
                      <span style={styles.statLabel}>Campaigns</span>
                    </div>
                  </div>

                  {/* Users list */}
                  <div style={styles.usersList}>
                    {users.map((u) => {
                      const userChars = getCharactersForUser(u.id);
                      return (
                        <div key={u.id} style={styles.userCard}>
                          <div style={styles.userHeader}>
                            <div style={styles.userInfo}>
                              <span style={styles.userName}>{u.displayName}</span>
                              <span style={styles.userId}>ID: {u.id.slice(0, 8)}...</span>
                            </div>
                            <span style={styles.charCount}>
                              {userChars.length} character{userChars.length !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {userChars.length > 0 && (
                            <div style={styles.charList}>
                              {userChars.map((char) => (
                                <div key={char.id} style={styles.charItem}>
                                  <div style={styles.charInfo}>
                                    <span style={styles.charName}>{formatCharacterInfo(char)}</span>
                                  </div>

                                  {/* Campaign dropdown */}
                                  <div style={styles.reassignSection}>
                                    <select
                                      value={char.campaignId || ''}
                                      onChange={(e) => {
                                        const newCampaignId = e.target.value || null;
                                        if (newCampaignId === (char.campaignId || null)) return;
                                        setPendingAction({
                                          kind: 'changeCampaign',
                                          characterId: char.id,
                                          characterLabel: formatCharacterInfo(char),
                                          fromCampaignName: campaignNameById(char.campaignId),
                                          toCampaignId: newCampaignId,
                                          toCampaignName: campaignNameById(newCampaignId),
                                        });
                                      }}
                                      disabled={changingCampaign === char.id}
                                      style={styles.selectCampaign}
                                    >
                                      <option value="">No Campaign</option>
                                      {campaigns.map((c) => (
                                        <option key={c.id} value={c.id}>
                                          {c.name}
                                        </option>
                                      ))}
                                    </select>
                                    {changingCampaign === char.id && (
                                      <Icon name="loader" size={14} style={{ marginLeft: 4 }} />
                                    )}
                                  </div>

                                  {/* Reassign user dropdown */}
                                  <div style={styles.reassignSection}>
                                    <select
                                      value={reassigning === char.id ? selectedUserId : ''}
                                      onChange={(e) => {
                                        const newUserId = e.target.value;
                                        if (!newUserId) return;
                                        const target = users.find((other) => other.id === newUserId);
                                        setPendingAction({
                                          kind: 'reassign',
                                          characterId: char.id,
                                          characterLabel: formatCharacterInfo(char),
                                          fromUserName: u.displayName,
                                          toUserId: newUserId,
                                          toUserName: target?.displayName ?? 'Unknown User',
                                        });
                                        // Reset the dropdown so it doesn't appear pre-selected
                                        // while awaiting confirmation.
                                        e.target.value = '';
                                      }}
                                      disabled={reassigning === char.id}
                                      style={styles.select}
                                    >
                                      <option value="">Reassign to...</option>
                                      {users.filter((other) => other.id !== u.id).map((other) => (
                                        <option key={other.id} value={other.id}>
                                          {other.displayName}
                                        </option>
                                      ))}
                                    </select>
                                    {reassigning === char.id && (
                                      <Icon name="loader" size={14} style={{ marginLeft: 4 }} />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {userChars.length === 0 && (
                            <div style={styles.noCharsRow}>
                              <span style={styles.noChars}>No characters</span>
                              {/* Add to campaign directly */}
                              <select
                                onChange={async (e) => {
                                  if (e.target.value) {
                                    const select = e.target;
                                    try {
                                      await ensureCampaignMember(select.value, u.id);
                                      select.value = '';
                                      alert(`Added ${u.displayName} to campaign`);
                                    } catch (err) {
                                      setError((err as Error).message);
                                    }
                                  }
                                }}
                                style={styles.selectCampaign}
                              >
                                <option value="">Add to campaign...</option>
                                {campaigns.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Refresh button */}
                  <button onClick={loadData} style={styles.refreshButton}>
                    <Icon name="refresh-cw" size={14} />
                    Refresh Data
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* Confirmation gate for destructive character moves */}
        {pendingAction && (
          <div
            style={styles.confirmOverlay}
            onClick={(e) => {
              e.stopPropagation();
              setPendingAction(null);
            }}
          >
            <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.confirmTitle}>
                <Icon name="alert" size={18} color={GOLD} />
                {pendingAction.kind === 'reassign' ? 'Reassign Character?' : 'Change Campaign?'}
              </h3>
              {pendingAction.kind === 'reassign' ? (
                <p style={styles.confirmBody}>
                  Move <strong style={{ color: 'var(--color-text)' }}>{pendingAction.characterLabel}</strong>{' '}
                  from owner <strong style={{ color: 'var(--color-text)' }}>{pendingAction.fromUserName}</strong>{' '}
                  to <strong style={{ color: GOLD }}>{pendingAction.toUserName}</strong>?
                  <br />
                  <span style={{ color: 'var(--color-text-muted)' }}>
                    This changes the character&rsquo;s owner and player name and reconciles campaign
                    membership. It cannot be undone.
                  </span>
                </p>
              ) : (
                <p style={styles.confirmBody}>
                  Move <strong style={{ color: 'var(--color-text)' }}>{pendingAction.characterLabel}</strong>{' '}
                  from campaign <strong style={{ color: 'var(--color-text)' }}>{pendingAction.fromCampaignName}</strong>{' '}
                  to <strong style={{ color: GOLD }}>{pendingAction.toCampaignName}</strong>?
                  <br />
                  <span style={{ color: 'var(--color-text-muted)' }}>
                    This changes the character&rsquo;s campaign and reconciles campaign membership.
                    It cannot be undone.
                  </span>
                </p>
              )}
              <div style={styles.confirmActions}>
                <button onClick={() => setPendingAction(null)} style={styles.confirmCancel}>
                  Cancel
                </button>
                <button onClick={runPendingAction} style={styles.confirmConfirm}>
                  {pendingAction.kind === 'reassign' ? 'Reassign' : 'Change Campaign'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const GOLD = '#d4af37';

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: 'var(--spacing-lg)',
  },

  modal: {
    position: 'relative',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    border: `2px solid ${GOLD}`,
    width: '100%',
    maxWidth: 800,
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
  },

  header: {
    position: 'relative',
    padding: 'var(--spacing-lg)',
    borderBottom: `1px solid var(--color-border)`,
  },

  title: {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    fontSize: 'var(--font-size-xl)',
    fontFamily: 'var(--font-display)',
    color: GOLD,
  },

  closeIconButton: {
    position: 'absolute',
    top: 'var(--spacing-md)',
    right: 'var(--spacing-md)',
    padding: 'var(--spacing-xs)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
  },

  tabBar: {
    display: 'flex',
    gap: 0,
    borderBottom: '1px solid var(--color-border)',
    padding: '0 var(--spacing-lg)',
  },

  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
  },

  tabActive: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: `2px solid ${GOLD}`,
    color: GOLD,
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
  },

  content: {
    flex: 1,
    overflow: 'auto',
    padding: 'var(--spacing-lg)',
  },

  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-xl)',
    color: 'var(--color-text-secondary)',
  },

  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    border: '1px solid var(--color-danger)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-danger)',
  },

  stats: {
    display: 'flex',
    gap: 'var(--spacing-lg)',
    marginBottom: 'var(--spacing-lg)',
    justifyContent: 'center',
  },

  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 'var(--spacing-md)',
    backgroundColor: 'var(--color-surface-raised)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
    minWidth: 100,
  },

  statNumber: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 700,
    color: GOLD,
  },

  statLabel: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
  },

  userCard: {
    backgroundColor: 'var(--color-surface-raised)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
  },

  userHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-md)',
    backgroundColor: 'var(--color-background-elevated)',
  },

  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },

  userName: {
    fontWeight: 600,
    color: 'var(--color-text)',
  },

  userId: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    fontFamily: 'monospace',
  },

  charCount: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    backgroundColor: 'var(--color-surface)',
    padding: '4px 8px',
    borderRadius: 'var(--radius-sm)',
  },

  charList: {
    padding: 'var(--spacing-sm)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
  },

  charItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-sm)',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-sm)',
    gap: 'var(--spacing-sm)',
    flexWrap: 'wrap',
  },

  charInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
  },

  charName: {
    fontWeight: 500,
    color: 'var(--color-text)',
  },

  charCampaign: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
  },

  reassignSection: {
    display: 'flex',
    alignItems: 'center',
  },

  select: {
    padding: '6px 8px',
    backgroundColor: 'var(--color-surface-raised)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
    minWidth: 120,
  },

  selectCampaign: {
    padding: '6px 8px',
    backgroundColor: 'var(--color-surface-raised)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
    minWidth: 150,
  },

  noCharsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-md)',
  },

  noChars: {
    color: 'var(--color-text-muted)',
    fontSize: 'var(--font-size-sm)',
    fontStyle: 'italic',
  },

  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    marginTop: 'var(--spacing-lg)',
    backgroundColor: 'transparent',
    border: `1px solid ${GOLD}`,
    borderRadius: 'var(--radius-sm)',
    color: GOLD,
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  closeButton: {
    padding: 'var(--spacing-sm) var(--spacing-lg)',
    backgroundColor: GOLD,
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    color: '#1a1410',
    fontWeight: 600,
    cursor: 'pointer',
  },

  confirmOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    padding: 'var(--spacing-lg)',
    borderRadius: 'var(--radius-lg)',
  },

  confirmModal: {
    backgroundColor: 'var(--color-surface)',
    border: `2px solid ${GOLD}`,
    borderRadius: 'var(--radius-lg)',
    width: '100%',
    maxWidth: 420,
    padding: 'var(--spacing-lg)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
  },

  confirmTitle: {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    fontSize: 'var(--font-size-lg)',
    fontFamily: 'var(--font-display)',
    color: GOLD,
  },

  confirmBody: {
    margin: 0,
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6,
  },

  confirmActions: {
    display: 'flex',
    gap: 'var(--spacing-sm)',
    justifyContent: 'flex-end',
  },

  confirmCancel: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'transparent',
    color: 'var(--color-text-secondary)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
  },

  confirmConfirm: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: GOLD,
    color: '#1a1410',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 700,
    cursor: 'pointer',
  },
};
